import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import BackButton from '../../../components/back-button/BackButton';
import SortedTable from '../../../components/sorted-table/SortedTable';
import AnimalBanner from '../../../components/animal/AnimalBanner';
import ActionMenu, { ActionButton } from '../../../components/action-buttons/ActionButtons';
import QuickView from '../../../components/quick-view/QuickView';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import { EditSVG } from '../../../components/svgs';
import { SpecimenRow } from '../../inventory/InventoryItem';
import { AppRoles } from '../../../types/enums/appRoles.enum';
import { AccountRoles } from '../../../types/enums/accountRoles.enum';
import { InventorySource } from '../../../types/enums';
import { Specimen } from '../../../types/interfaces/specimen.interfaces';
import { Genders } from '../../../types/enums/genders.enum';
import { PermissionService } from '../../../services/permission.service';
import { showToast } from '../../../services/toast.service';
import { getOnFarmSpecimensByOwnership } from '../../../services/onFarmInventory.service';
import { ROUTE_PATHS, ON_FARM_PATHS } from '../../../constants/routePaths';
import { toastMessages } from '../../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
const OnFarmInventoryItem = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();

  const { accountId } = useAppSelector(state => state.activeAccount);
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const verified = useAppSelector(state => state.user.verified);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState<boolean>(false);
  const [quantityOwned, setQuantityOwned] = useState<number>();
  const [specimen, setSpecimen] = useState<Specimen>();
  const [specimens, setSpecimens] = useState<SpecimenRow[]>();

  const isFemale = specimens?.[0]?.specimen?.animal?.gender === Genders.Female;
  const isOnFarmLocked = useOnFarmActionBlocked();

  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);

  const handleGetSpecimens = useCallback(async () => {
    if (animalId && accountId) {
      try {
        setIsLoading(true);
        const { data: specs } = await getOnFarmSpecimensByOwnership(animalId, accountId);
        let owned = 0;

        const quants = specs.map((spec): SpecimenRow => {
          owned += spec.quantityOwned;
          return { specimen: spec, availableQty: spec.quantityAvailable, ownedQty: spec.quantityOwned };
        });

        setQuantityOwned(owned);
        setSpecimens(quants);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  }, [animalId, accountId]);

  useEffect(() => {
    handleGetSpecimens();
  }, [handleGetSpecimens]);

  const onEdit = () => {
    navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animalId + '/' + ON_FARM_PATHS.EDIT);
  };

  const onSell = () => {
    navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animalId + '/' + ON_FARM_PATHS.SELL_TRANSFER);
  };

  const onFarmUse = () => {
    navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animalId + '/' + ON_FARM_PATHS.FARM_USE);
  };

  const onWithdraw = () => {
    navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animalId + '/' + ON_FARM_PATHS.WITHDRAW);
  };

  const onDiscard = () => {
    navigate(ROUTE_PATHS.APP_ON_FARM_INVENTORY + '/' + animalId + '/' + ON_FARM_PATHS.DISCARD);
  };

  const handleQuickViewOpen = (item: Specimen) => {
    setSpecimen(item);
    setIsQuickViewOpen(true);
  };

  const getActionButtons = (row: SpecimenRow): ActionButton[] => [
    { name: BUTTON_CONSTANTS.QUICK_VIEW, action: () => handleQuickViewOpen(row.specimen) },
  ];

  const getInventoryItems = (headers: string[], className: string, heading: string) => {
    return (
      <div className={className}>
        <h1>{heading}</h1>
        <SortedTable
          headers={headers.map((header: string) => {
            return { displayName: header };
          })}
          data={specimens?.map((spec: SpecimenRow) => {
            const flushMating =
              spec.specimen.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ??
              '-';
            return className === 'mobile-view'
              ? [
                  { content: <SpecimenLotInfo specimenId={spec.specimen.specimenId} lotNumber={spec.specimen.lotNumber} /> },
                  { content: spec.availableQty },
                  { content: spec.ownedQty },
                  {
                    content: <ActionMenu actionButtons={getActionButtons(spec)} />,
                  },
                ]
              : [
                  { content: spec.specimen.specimenType?.name },
                  { content: spec.specimen.specimenUOM },
                  {
                    content: (
                      <SpecimenLotInfo
                        specimenId={spec.specimen.specimenId}
                        lotNumber={spec.specimen.lotNumber}
                        freezeDate={spec.specimen.freezeDate}
                      />
                    ),
                  },
                  {
                    content: isFemale ? flushMating : spec.specimen.quality ?? '-',
                  },
                  { content: spec.availableQty },
                  { content: spec.ownedQty },
                  {
                    content: <ActionMenu actionButtons={getActionButtons(spec)} />,
                  },
                ];
          })}
          loading={isLoading}
          noResultsMessage={LABEL_CONSTANTS.NO_INVENTORY}
        />
      </div>
    );
  };

  return (
    <div className="inventory-item on-farm-inventory-item">
      <BackButton />

      <AnimalBanner
        animalId={animalId}
        inventorySource={InventorySource.ON_FARM}
        headerAction={
          <button
            className="edit-animal-button"
            type="button"
            onClick={onEdit}
            title={BUTTON_CONSTANTS.EDIT}
            aria-label={BUTTON_CONSTANTS.EDIT}
            disabled={isOnFarmLocked}>
            <EditSVG />
          </button>
        }
      >
        <div className="quantity">
          <h3>{LABEL_CONSTANTS.QUANTITY_OWNED}:</h3>
          <span>
            {' '}
            {quantityOwned ?? '--'} {LABEL_CONSTANTS.UNITS}
          </span>
        </div>

        <div className="action-buttons">
          {permissionService.userHasInventoryPermission() ? (
            <>
              <button
                className="button medium blue inverted"
                type="button"
                onClick={onSell}
                disabled={isOnFarmLocked}>
                {BUTTON_CONSTANTS.SELL_TRANSFER}
              </button>
              <button
                className="button medium blue inverted"
                type="button"
                onClick={onWithdraw}
                disabled={isOnFarmLocked}>
                {BUTTON_CONSTANTS.WITHDRAW}
              </button>
              <button
                className="button small blue inverted"
                type="button"
                onClick={onFarmUse}
                disabled={isOnFarmLocked}>
                {BUTTON_CONSTANTS.USE}
              </button>
              <button
                className="button small red inverted"
                type="button"
                onClick={onDiscard}
                disabled={isOnFarmLocked}>
                {BUTTON_CONSTANTS.DISCARD}
              </button>
            </>
          ) : (
            <>
              <br />
              <br />
            </>
          )}
        </div>
      </AnimalBanner>

      <div className="inventory-items">
        {getInventoryItems(
          [
            TABLE_HEADER_CONSTANTS.INVENTORY_TYPE,
            TABLE_HEADER_CONSTANTS.UNIT_OF_MEASURE,
            TABLE_HEADER_CONSTANTS.LOT_DATE_NO,
            isFemale ? TABLE_HEADER_CONSTANTS.FLUSH_MATING : TABLE_HEADER_CONSTANTS.QUALITY,
            isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY,
            TABLE_HEADER_CONSTANTS.OWNED_QTY,
            TABLE_HEADER_CONSTANTS.ACTION_ITEM,
          ],
          'desktop-view',
          LABEL_CONSTANTS.INVENTORY_ITEMS,
        )}
        {getInventoryItems(
          [
            TABLE_HEADER_CONSTANTS.LOT_NO,
            isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY,
            TABLE_HEADER_CONSTANTS.OWNED_QTY,
            TABLE_HEADER_CONSTANTS.ACTION_ITEM,
          ],
          'mobile-view',
          LABEL_CONSTANTS.COLLECTION_HISTORY,
        )}
      </div>

      {specimen && (
        <QuickView
          specimen={specimen}
          isOpen={isQuickViewOpen}
          onClose={() => setIsQuickViewOpen(false)}
          setIsOpen={setIsQuickViewOpen}
        />
      )}
    </div>
  );
};

export default OnFarmInventoryItem;
